import { createContext, useContext, useMemo, useState, useRef } from 'react';
import { EditorProvider, useCurrentEditor, useEditorState } from '@tiptap/react';
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import { Placeholder } from '@tiptap/extensions/placeholder';
import { Extension } from '@tiptap/core';
import { CustomHighlight } from './placeholderExtension';
import { ExternalValueSync } from './ExternalValueSync';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import { Typography } from '@components/Typography';
import AIAssistant from '@features/AIAssistant';
import { useTranslation } from '@hooks';
import { serializePlaceholdersToHtml, parseTipTapJSONToPlainText, getEditorContentLength } from '@helpers/text-editor';
import {
    DEFAULT_PLACEHOLDER_TOKENS,
    getPlaceholderByToken,
    getPlaceholderTokensForContext,
} from '@constants/placeholders';
import { CssFormHelperText } from '@components/Input';

const PlaceholderContext = createContext(null);

export const usePlaceholderContext = () => useContext(PlaceholderContext);

export { useInsertPlaceholder } from './useInsertPlaceholder';

const CssCharCounter = styled(Typography)({
    position: 'absolute',
    bottom: 33.5,
    right: 14,
});

const Asterisc = styled('span')(() => ({
    color: 'var(--error-main)',
    fontWeight: 500,
}));

const Legend = styled('span', {
    shouldForwardProp: prop => prop !== 'isError',
})(({ isError }) => ({
    height: '11px',
    color: isError ? 'var(--error-main)' : 'var(--text-secondary)',
    fontFamily: 'Rubik',
    fontSize: '14px',
    lineHeight: '14px',
    letterSpacing: '0.38px',
    transform: 'scale(0.8)',
    background: 'white',
    position: 'absolute',
    left: '5px',
    top: '-7px',
    padding: '0 3px',
    zIndex: 3,
}));

const Container = styled('div', {
    shouldForwardProp: prop => prop !== 'hasNoLimit' && prop !== 'isEditable' && prop !== 'isError',
})(({ hasNoLimit, isEditable, isError }) => ({
    width: '100%',
    minHeight: '104px',
    padding: hasNoLimit ? '16.5px 14px' : '16.5px 14px 38px',
    borderRadius: 8,
    border: `1px solid ${isError ? 'var(--error-main)' : 'var(--divider)'} ${isEditable ? '' : '!important'}`,
    '&:hover': {
        border: `1px solid ${isError ? 'var(--error-main)' : 'var(--primary-main)'}`,
    },
    '&:focus-within': {
        border: `2px solid ${isError ? 'var(--error-main)' : 'var(--primary-main)'}`,
        width: 'calc(100%)',
        padding: hasNoLimit ? '15.5px 13px' : '15.5px 13px 37px',
    },
    '& .ProseMirror': {
        width: '100%',
        maxWidth: '100%',
        minWidth: '100%',
        overflow: 'auto',
        minHeight: '47.5px',
        outline: 'none',
        // Greyed text when disabled, matching the disabled MUI input look.
        color: isEditable ? 'var(--text-primary)' : 'var(--text-tertiary)',
        cursor: isEditable ? 'text' : 'not-allowed',
        fontFamily: 'Rubik, sans-serif',
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: '14px',
        lineHeight: '16px',
        letterSpacing: '0.15px',
        '& p': {
            margin: 0,
        },
    },
    '& .pl-custom-highlight, & .pl-locked-link-chip': {
        backgroundColor: '#bcc9ce',
        padding: '0px 5px',
        borderRadius: 8,
    },
    '& .pl-locked-link-chip': {
        marginLeft: 4,
        userSelect: 'none',
    },
    '& p.is-editor-empty:first-of-type::before': {
        color: 'var(--text-secondary)',
        fontWeight: '400',
        fontSize: ' 0.875rem',
        content: 'attr(data-placeholder)',
        float: 'left',
        height: '0',
        pointerEvents: 'none',
    },
    '& .ProseMirror-focused p.is-editor-empty:first-of-type::before': {
        content: 'none',
        display: 'none',
    },
}));

const InputBlurWrapper = styled('div')(() => ({
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: 'calc(100% - 16px)',
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
    pointerEvents: 'none',
    '& .MuiCircularProgress-root': {
        color: 'rgba(0, 0, 0, 0.38) !important',
    },
}));

const selectDoc = ({ editor }) => editor.state.doc;

const useEditorPlainText = editor => {
    const doc = useEditorState({ editor, selector: selectDoc, equalityFn: Object.is });
    return useMemo(() => parseTipTapJSONToPlainText(doc.toJSON()), [doc]);
};

const Counter = ({ limit, tokens, getLength = getEditorContentLength }) => {
    const { editor } = useCurrentEditor();
    const plainText = useEditorPlainText(editor);
    const length = getLength(plainText, tokens);

    return (
        <CssCharCounter
            variant="caption"
            sx={{
                color: length > limit ? 'var(--error-main)' : 'var(--text-secondary)',
            }}
        >
            {length}/{limit}
        </CssCharCounter>
    );
};

const AIExtension = ({
    onChange,
    btnStyles,
    isLoading,
    setIsLoading,
    editable,
    additionalOptions,
    contextKey,
    serializerOpts,
}) => {
    const { editor } = useCurrentEditor();
    const plainText = useEditorPlainText(editor);
    return (
        <AIAssistant
            btnStyles={btnStyles}
            value={plainText}
            setValue={newValue => {
                editor?.commands.setContent(serializePlaceholdersToHtml(newValue, serializerOpts), {
                    emitUpdate: false,
                });
                onChange(JSON.stringify(editor.getJSON()));
            }}
            disabled={!editable || isLoading}
            setLoading={setIsLoading}
            additionalOptions={additionalOptions}
            contextKey={contextKey}
        />
    );
};

/* INFO: handle keydown for editor because of tiptap bug
    https://github.com/ueberdosis/tiptap/issues/2403#issuecomment-1062712162
*/

const EditorContainer = ({ children, editorRef, onKeyDown = null, ...rest }) => {
    const handleKeyDown = e => {
        if (onKeyDown) {
            onKeyDown(e, editorRef.current);
        }
    };
    return (
        <Container onKeyDown={handleKeyDown} {...rest}>
            {children}
        </Container>
    );
};

const TextEditor = ({
    containerRef = null,
    ref,
    placeholderContext,
    getPlaceholderLabel,
    initialValue = '',
    onChange,
    label,
    limit,
    editable = true,
    error,
    helperText,
    aiAssistant,
    toolbar,
    required = false,
    placeholder,
    containerProps,
    extraExtensions,
    getLength = getEditorContentLength,
    ...rest
}) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const editorRef = useRef(null);

    const serializerOpts = useMemo(() => {
        const labelFor = token => {
            const def = getPlaceholderByToken(token);
            return def ? t(def.labelKey) : token;
        };
        return {
            tokens: placeholderContext
                ? getPlaceholderTokensForContext(placeholderContext)
                : DEFAULT_PLACEHOLDER_TOKENS,
            getLabel: getPlaceholderLabel || labelFor,
        };
    }, [placeholderContext, getPlaceholderLabel, t]);
    const { tokens, getLabel } = serializerOpts;

    const extensions = [
        Document,
        Paragraph,
        Text,
        HardBreak,
        CustomHighlight.configure({
            HTMLAttributes: {
                class: 'pl-custom-highlight',
            },
            tokens,
            getLabel,
        }),
        Placeholder.configure({
            placeholder: placeholder,
        }),
        Extension.create({
            addKeyboardShortcuts() {
                return {
                    'Mod-Enter': () => true,
                };
            },
        }),
        ...(extraExtensions || []),
    ];

    const hasNoExtension = !limit || limit === Infinity;

    return (
        <PlaceholderContext.Provider value={placeholderContext}>
            <div ref={containerRef} style={{ paddingBottom: '16px', position: 'relative' }} {...rest}>
                {label && (
                    <Legend isError={!!error}>
                        {label} {required && <Asterisc>*</Asterisc>}
                    </Legend>
                )}
                {isLoading && (
                    <InputBlurWrapper>
                        <CircularProgress />
                    </InputBlurWrapper>
                )}
                <EditorContainer
                    editorRef={editorRef}
                    hasNoLimit={hasNoExtension && !toolbar && !aiAssistant}
                    isEditable={editable && !isLoading}
                    isError={!!error}
                    className="editor-container"
                    {...containerProps}
                >
                    <EditorProvider
                        ref={ref}
                        {...rest}
                        placeholder={editable ? 'Type here...' : ''}
                        editable={editable && !isLoading}
                        extensions={extensions}
                        content={serializePlaceholdersToHtml(initialValue, serializerOpts)}
                        editorProps={{ attributes: { tabindex: !editable ? '-1' : undefined } }}
                        onUpdate={({ editor }) => {
                            onChange(editor.isEmpty ? '' : JSON.stringify(editor.getJSON()));
                            editorRef.current = editor;
                        }}
                    >
                        <ExternalValueSync value={initialValue} serializerOpts={serializerOpts} />
                        <div
                            className="editor-toolbar"
                            style={{
                                position: 'absolute',
                                bottom: 32.5,
                                left: 12,
                                display: 'flex',
                                gap: 6,
                                width: 'calc(100% - 24px)',
                                alignItems: 'flex-end',
                                pointerEvents: isLoading ? 'none' : undefined,
                            }}
                        >
                            {aiAssistant && (
                                <AIExtension
                                    onChange={onChange}
                                    isLoading={isLoading}
                                    setIsLoading={setIsLoading}
                                    editable={editable}
                                    additionalOptions={aiAssistant.additionalOptions || []}
                                    contextKey={aiAssistant.contextKey}
                                    serializerOpts={serializerOpts}
                                />
                            )}
                            {toolbar && toolbar}
                        </div>
                        {!hasNoExtension && (
                            <Counter limit={limit} tokens={serializerOpts.tokens} getLength={getLength} />
                        )}
                        {helperText && <CssFormHelperText error>{helperText}</CssFormHelperText>}
                    </EditorProvider>
                </EditorContainer>
            </div>
        </PlaceholderContext.Provider>
    );
};

export default TextEditor;
